import {
  Button,
  TextField,
  Box,
  Grid,
  Paper,
  useMediaQuery,
  Autocomplete,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Form, Formik } from 'formik';

import { TENANT_MODULE_OPTIONS } from '../../utils/tenantModuleCodes';

const MODULE_OPTIONS = [...TENANT_MODULE_OPTIONS];

const parseModules = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return value
    .split(',')
    .map((x) => x.trim())
    .filter((x) => !!x && (MODULE_OPTIONS as readonly string[]).includes(x));
};

const LicenseForm = (props: any) => {
  const { entity, onSubmit, errors, handleCancel, readonly = false } = props;
  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );

  return (
    <Formik
      initialValues={{
        ...entity,
        includedModules: parseModules(entity.includedModuleCodes),
      }}
      enableReinitialize={true}
      validate={(values) => {
        const validation: any = {};
        if (!values.name) validation.name = 'Required';
        if (!values.description) validation.description = 'Required';
        if (!values.maxDevices || values.maxDevices <= 0) {
          validation.maxDevices = 'Must be greater than 0';
        }
        return validation;
      }}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(
          {
            ...values,
            includedModuleCodes: (values.includedModules ?? []).join(','),
          },
          setSubmitting
        );
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        handleChange,
        setFieldValue,
        errors: validationErrors,
      }) => (
        <Form>
          <Paper>
            <Grid container spacing={2} sx={{ justifyContent: 'left', p: 5 }}>
              <h2>{values.id ? 'Edit' : 'Create'} License</h2>
              <Grid size={12} sx={{ pt: 2 }}>
                <Box sx={{ color: 'red' }}>
                  {(errors ?? []).map((error: string) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.name ?? ''}
                  onChange={handleChange}
                  label="Name"
                  name="name"
                  required
                  disabled={readonly}
                  fullWidth
                  error={!!touched.name && !!validationErrors.name}
                  helperText={
                    !!touched.name &&
                    !!validationErrors.name &&
                    (validationErrors.name as string)
                  }
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.description ?? ''}
                  onChange={handleChange}
                  label="Description"
                  name="description"
                  required
                  disabled={readonly}
                  fullWidth
                  multiline
                  minRows={2}
                  error={!!touched.description && !!validationErrors.description}
                  helperText={
                    !!touched.description &&
                    !!validationErrors.description &&
                    (validationErrors.description as string)
                  }
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.maxDevices ?? 0}
                  onChange={handleChange}
                  label="Max device count"
                  name="maxDevices"
                  type="number"
                  required
                  disabled={readonly}
                  fullWidth
                  error={!!touched.maxDevices && !!validationErrors.maxDevices}
                  helperText={
                    !!touched.maxDevices &&
                    !!validationErrors.maxDevices &&
                    (validationErrors.maxDevices as string)
                  }
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  multiple
                  options={MODULE_OPTIONS}
                  value={values.includedModules ?? []}
                  disabled={readonly}
                  onChange={(_e, newValue) =>
                    setFieldValue('includedModules', newValue)
                  }
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Included modules" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.isPublished}
                      disabled={readonly}
                      onChange={(e) => setFieldValue('isPublished', e.target.checked)}
                    />
                  }
                  label="Publikált (wizardban látható)"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rendezési sorrend"
                  name="sortOrder"
                  value={values.sortOrder ?? 0}
                  disabled={readonly}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Próbaidő (nap)"
                  name="trialDays"
                  helperText="Üres = nincs próbaidő"
                  value={values.trialDays ?? ''}
                  disabled={readonly}
                  onChange={handleChange}
                />
              </Grid>
              <Grid container sx={{ pt: 3, justifyContent: 'left' }}>
                {!readonly && (
                  <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{ p: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }} sx={{ p: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth={isSmallScreen}
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  );
};

export default LicenseForm;
